import { memo, useEffect, useRef } from 'react';

import {
  usePlayerActions,
  usePlayerActorScene,
  usePlayerStore,
} from '@cartwhl/3d/player';
import Text from '@cartwhl/ui/base/TextNew';
import CoordinatesIcon from '@cartwhl/ui/icons/CoordinatesIcon';
import RotateIcon from '@cartwhl/ui/icons/RotateIcon';
import Overlay from '@cartwhl/ui/player/Overlay';

import { AdvancedIcon } from 'components/MotionEditor/components/icons';

import { useStore as useMotionEditorStore } from '../../motionEditorStore';
import { isKeyPoseFkJointId } from '../fk/keyPoseFkConfig';
import { useCurrentFrameKeyPoseRenderState } from '../hooks/useCurrentFrameKeyPoseRenderState';
import { isKeyPoseIkEffectorId } from '../ik/keyPoseIkConfig';
import { resolveActiveKeyPoseReferenceName } from '../keyPoseMotionUtils';
import { hasKeyPoseBoneSelection } from '../keyPoseSelectors';
import { useStore } from '../keyPoseStore';
import KeyPoseStickFigureSelector, {
  type KeyPoseStickFigureJointId,
  type KeyPoseStickFigureMode,
  resolveKeyPoseStickFigureSkeletonType,
} from './KeyPoseStickFigureSelector';

export function KeyPoseControlsOptions() {
  return (
    <>
      <CurrentFrameKeyPoseSelectionReset />
      <Overlay.Group orientation="vertical">
        <Text token="12/medium" className="text-primary mb-1">
          Select joint:
        </Text>
        <KeyPoseControlsBody />
      </Overlay.Group>
    </>
  );
}

function KeyPoseControlsOverlay() {
  return (
    <Overlay.Panel
      placement="top-left"
      tooltipSide="top"
      widthClassName="w-[170px]"
      collapsible
    >
      <Overlay.PanelHeader icon={<AdvancedIcon />} title="Edit controls" />
      <Overlay.Content>
        <KeyPoseControlsOptions />
      </Overlay.Content>
    </Overlay.Panel>
  );
}

function CurrentFrameKeyPoseSelectionReset() {
  const playerStore = usePlayerStore();

  if (playerStore.state.transport.isPlaying) {
    return null;
  }

  return <PausedCurrentFrameKeyPoseSelectionReset />;
}

function PausedCurrentFrameKeyPoseSelectionReset() {
  const keyPoseStore = useStore();
  const { editableKeyPoseId, showKeyPoseGizmos } =
    useCurrentFrameKeyPoseRenderState({});
  const previousEditableKeyPoseIdRef = useRef<string | null>(null);
  const hasEditingSelection = hasKeyPoseBoneSelection(
    keyPoseStore.state.editing,
  );

  useEffect(() => {
    const previousEditableKeyPoseId = previousEditableKeyPoseIdRef.current;
    previousEditableKeyPoseIdRef.current = editableKeyPoseId;
    const movedAwayFromEditableKeyPose =
      previousEditableKeyPoseId !== null &&
      previousEditableKeyPoseId !== editableKeyPoseId;

    if (
      movedAwayFromEditableKeyPose ||
      (hasEditingSelection && !showKeyPoseGizmos)
    ) {
      keyPoseStore.clearKeyPoseEditingSelection();
    }
  }, [editableKeyPoseId, hasEditingSelection, keyPoseStore, showKeyPoseGizmos]);

  return null;
}

function KeyPoseControlsBody() {
  const keyPoseStore = useStore();
  const motionEditorStore = useMotionEditorStore();
  const playerActions = usePlayerActions();
  const referenceName = resolveActiveKeyPoseReferenceName(
    keyPoseStore.state.activeReferenceName,
    motionEditorStore.state.playback.selectedReferenceName,
    motionEditorStore.state.playback.sceneActors,
  );
  const actorScene = usePlayerActorScene(referenceName ?? undefined);
  const stickFigureSkeletonType =
    resolveKeyPoseStickFigureSkeletonType(actorScene);
  const { editing } = keyPoseStore.state;
  const mode = editing.mode;
  const hoveredJointId =
    mode === 'fk' ? editing.hoveredFkJointId : editing.hoveredIkEffectorId;
  const selectedJointId =
    mode === 'fk' ? editing.selectedFkJointId : editing.selectedIkEffectorId;

  const handleModeChange = (nextMode: KeyPoseStickFigureMode) => {
    keyPoseStore.setKeyPoseEditingMode(nextMode);
  };

  const handleLockFeetChange = (checked: boolean) => {
    keyPoseStore.setKeyPoseLockFeet(checked);
  };

  const handleHoveredJointChange = (
    jointId: KeyPoseStickFigureJointId | null,
  ) => {
    if (mode === 'ik') {
      keyPoseStore.setKeyPoseIkHoveredEffector(
        jointId && isKeyPoseIkEffectorId(jointId) ? jointId : null,
      );
      return;
    }

    keyPoseStore.setKeyPoseFkHoveredJoint(
      jointId && isKeyPoseFkJointId(jointId) ? jointId : null,
    );
  };

  const handleSelectedJointChange = (jointId: KeyPoseStickFigureJointId) => {
    if (mode === 'ik') {
      if (!isKeyPoseIkEffectorId(jointId)) {
        keyPoseStore.setKeyPoseIkSelectedEffector(null);
        return;
      }

      playerActions.pause();
      keyPoseStore.setKeyPoseIkSelectedEffector(jointId);
      return;
    }

    if (!isKeyPoseFkJointId(jointId)) {
      keyPoseStore.setKeyPoseFkSelectedJoint(null);
      return;
    }

    playerActions.pause();
    keyPoseStore.setKeyPoseFkSelectedJoint(jointId);
  };

  return (
    <Overlay.Group orientation="vertical" className="gap-2">
      <KeyPoseStickFigureSelector
        hoveredJointId={hoveredJointId}
        mode={mode}
        selectedJointId={selectedJointId}
        skeletonType={stickFigureSkeletonType}
        toolOptions={[
          {
            active: mode === 'ik',
            icon: <CoordinatesIcon className="size-5" />,
            label: 'Move (W)',
            onClick: () => handleModeChange('ik'),
          },
          {
            active: mode === 'fk',
            icon: <RotateIcon className="size-4" />,
            label: 'Rotate (E)',
            onClick: () => handleModeChange('fk'),
          },
        ]}
        onHoveredJointChange={handleHoveredJointChange}
        onSelectedJointChange={handleSelectedJointChange}
      />
      <Overlay.Divider />
      <Overlay.CheckboxButton
        checked={editing.lockFeet}
        checkboxPlacement="start"
        className="h-[38px]"
        onCheckedChange={handleLockFeetChange}
      >
        Lock feet
      </Overlay.CheckboxButton>
    </Overlay.Group>
  );
}

const MemoizedKeyPoseControlsOverlay = Object.assign(
  memo(KeyPoseControlsOverlay),
  {
    overlayPlacement: 'top-left',
  },
);

export default MemoizedKeyPoseControlsOverlay;
